import React, { useState } from 'react'
import AutoResizeTextarea from '../shared/AutoResizeTextarea'
import UserAvatar from '../shared/UserAvatar'

const TAG_KEYS = ['patient', 'fun', 'professional', 'fast_progress', 'well_prepared', 'interactive']
const TAG_DEFAULTS = { patient: 'Patient', fun: 'Fun', professional: 'Professional', fast_progress: 'Fast Progress', well_prepared: 'Well Prepared', interactive: 'Engaging' }
const TAG_API_VALUES = { patient: '耐心', fun: '有趣', professional: '專業', fast_progress: '進步快', well_prepared: '準備充分', interactive: '互動好' }

export default function ReviewModal({ teacher, onClose, onSubmitted, translations = {} }) {
  const [rating, setRating] = useState(0)
  const [hoverRating, setHoverRating] = useState(0)
  const [selectedTagKeys, setSelectedTagKeys] = useState([])
  const [comment, setComment] = useState('')
  const [isAnonymous, setIsAnonymous] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const t = translations.form || {}
  const tagTranslations = translations.tags || {}

  const toggleTag = (tagKey) => {
    if (selectedTagKeys.includes(tagKey)) {
      setSelectedTagKeys(selectedTagKeys.filter(k => k !== tagKey))
    } else if (selectedTagKeys.length < 3) {
      setSelectedTagKeys([...selectedTagKeys, tagKey])
    }
  }

  const handleSubmit = async () => {
    if (rating === 0) {
      setError(t.rating_required || 'Please select a rating.')
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch('/api/teacher_reviews', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          teacher_profile_id: teacher.teacherProfileId,
          teaching_team_id: teacher.teachingTeamId,
          rating,
          tags: selectedTagKeys.map(k => TAG_API_VALUES[k]),
          comment: comment.trim() || null,
          is_anonymous: isAnonymous,
        }),
      })

      const data = await res.json()

      if (data.success) {
        onSubmitted(teacher, data.review)
      } else {
        setError(data.error || data.errors?.join(', ') || t.submit_failed || 'Submission failed')
      }
    } catch {
      setError(t.network_error || 'Network error. Please try again later.')
    } finally {
      setSubmitting(false)
    }
  }

  const displayRating = hoverRating || rating

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 lg:p-4" onClick={onClose}>
      <div
        className="bg-white w-full h-full lg:h-auto lg:max-h-[90vh] lg:max-w-lg lg:mx-4 lg:rounded-xl lg:shadow-xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header. The standard blue bar: title and close only, so it matches
            every other modal in the app. The teacher's identity sits at the top
            of the body instead, where an avatar does not crowd the bar. */}
        <div
          className="px-6 py-4 border-b flex items-center justify-between flex-shrink-0"
          style={{ backgroundColor: '#E6F0FF' }}
        >
          <h3 className="text-lg font-semibold" style={{ color: '#3366FF' }}>{t.title || 'Review Teacher'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 cursor-pointer">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body. Only this scrolls, so the actions below stay reachable on a
            short screen. */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div className="flex items-center gap-3">
            <UserAvatar
              src={teacher.teacherAvatarUrl}
              name={teacher.teacherName}
              size="sm"
            />
            <div className="min-w-0">
              <div className="font-medium text-gray-900">{teacher.teacherName}</div>
              {teacher.courseName && <div className="text-xs text-gray-500">{teacher.courseName}</div>}
            </div>
          </div>

          {/* Star Rating */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t.rating_label || 'Rating'} <span className="text-red-500">*</span></label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className="cursor-pointer"
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setRating(star)}
                >
                  <svg
                    className={`w-8 h-8 transition-colors ${star <= displayRating ? 'text-yellow-400' : 'text-gray-300'}`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </button>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t.tags_label || 'Quick Tags'} <span className="text-gray-400 text-xs">{t.tags_hint || '(up to 3)'}</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {TAG_KEYS.map((tagKey) => (
                <button
                  key={tagKey}
                  type="button"
                  onClick={() => toggleTag(tagKey)}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-colors cursor-pointer ${
                    selectedTagKeys.includes(tagKey)
                      ? 'bg-[#3366FF] text-white border-[#3366FF]'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-[#3366FF]'
                  }`}
                >
                  {tagTranslations[tagKey] || TAG_DEFAULTS[tagKey]}
                </button>
              ))}
            </div>
          </div>

          {/* Comment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t.comment_label || 'Written Review (optional)'}</label>
            <AutoResizeTextarea
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, 1000))}
              placeholder={t.comment_placeholder || 'Share your lesson experience...'}
              className="w-full border border-gray-300 rounded-lg p-3 text-sm resize-none focus:ring-2 focus:ring-[#3366FF] focus:border-transparent"
              rows={3}
            />
            <div className="text-xs text-gray-400 text-right mt-1">{comment.length}/1000</div>
          </div>

          {/* Anonymity */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isAnonymous}
              onChange={(e) => setIsAnonymous(e.target.checked)}
              className="w-4 h-4 text-[#3366FF] rounded"
            />
            <span className="text-sm text-gray-700">{t.anonymous_label || 'Anonymous Review'}</span>
          </label>

          {/* Warning */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</div>
          )}
        </div>

        {/* Footer. Outside the scrolling region on purpose. */}
        <div className="px-6 py-4 border-t flex gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
          >
            {t.cancel || 'Cancel'}
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || rating === 0}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-[#3366FF] rounded-lg hover:bg-[#2952CC] transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {submitting ? (t.submitting || 'Submitting...') : (t.submit || 'Submit Review')}
          </button>
        </div>
      </div>
    </div>
  )
}
